import React, { useState } from 'react'
import { FileAudio, Play, Pause, Download, Share, Trash2, Edit3, Search, Filter, Grid, List } from 'lucide-react'
import { useApp } from '../context/AppContext.jsx'
import AudioCard from '../components/AudioCard.jsx'
import AudioModal from '../components/AudioModal.jsx'
import toast from 'react-hot-toast'

function MyAudioFiles() {
  const {
    audioFiles,
    deleteAudioFile,
    updateAudioFile,
    currentAudio,
    isPlaying,
    playAudio,
    pauseAudio
  } = useApp()

  const [searchTerm, setSearchTerm] = useState('')
  const [sortBy, setSortBy] = useState('newest')
  const [filterBy, setFilterBy] = useState('all')
  const [viewMode, setViewMode] = useState('grid')
  const [editingAudio, setEditingAudio] = useState(null)
  const [showAudioModal, setShowAudioModal] = useState(false)

  // Filter and sort audio files
  const filteredAndSortedFiles = audioFiles
    .filter(audio => {
      const matchesSearch = audio.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        audio.text.toLowerCase().includes(searchTerm.toLowerCase())

      if (filterBy === 'all') return matchesSearch
      if (filterBy === 'male') return matchesSearch && audio.voiceSettings?.gender === 'male'
      if (filterBy === 'female') return matchesSearch && audio.voiceSettings?.gender === 'female'

      return matchesSearch
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'newest':
          return new Date(b.createdAt) - new Date(a.createdAt)
        case 'oldest':
          return new Date(a.createdAt) - new Date(b.createdAt)
        case 'title':
          return a.title.localeCompare(b.title)
        case 'duration':
          return (b.duration || 0) - (a.duration || 0)
        default:
          return 0
      }
    })

  const handlePlayPause = (audio) => {
    if (currentAudio?.id === audio.id) {
      if (isPlaying) {
        pauseAudio()
      } else {
        playAudio(audio)
      }
    } else {
      playAudio(audio)
    }
  }

  const handleDownload = async (audio) => {
    try {
      const response = await fetch(audio.audioUrl)
      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `${audio.title}.wav`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
      toast.success('Audio file downloaded!')
    } catch (error) {
      toast.error('Failed to download audio file')
      console.error(error)
    }
  }

  const handleShare = async (audio) => {
    try {
      if (navigator.share) {
        await navigator.share({
          title: audio.title,
          text: `Check out this audio: "${audio.text.substring(0, 100)}..."`
        })
      } else {
        // Fallback - copy to clipboard
        await navigator.clipboard.writeText(`${audio.title}: ${audio.text}`)
        toast.success('Audio details copied to clipboard!')
      }
    } catch (error) {
      toast.error('Failed to share audio')
      console.error(error)
    }
  }

  const handleEdit = (audio) => {
    setEditingAudio(audio)
    setShowAudioModal(true)
  }

  const handleDelete = (audio) => {
    if (window.confirm(`Are you sure you want to delete "${audio.title}"?`)) {
      deleteAudioFile(audio.id)
    }
  }

  const handleSaveEdit = (audioData) => {
    updateAudioFile(editingAudio.id, audioData)
    setEditingAudio(null)
    setShowAudioModal(false)
  }

  const formatDuration = (duration) => {
    if (!duration) return '0:00'
    const minutes = Math.floor(duration / 60)
    const seconds = Math.floor(duration % 60)
    return `${minutes}:${seconds.toString().padStart(2, '0')}`
  }

  const formatFileSize = (size) => {
    if (!size) return 'Unknown'
    if (size < 1024) return `${size} B`
    if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`
    return `${(size / (1024 * 1024)).toFixed(1)} MB`
  }

  return (
    <div className="max-w-7xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          My Audio Files
        </h1>
        <p className="text-gray-600">
          Manage, organize, and share your saved audio recordings.
        </p>
      </div>

      {/* Controls */}
      <div className="card mb-8">
        <div className="flex flex-col lg:flex-row gap-4">
          {/* Search */}
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search audio files..."
                className="input-field pl-10"
              />
            </div>
          </div>

          {/* Filters */}
          <div className="flex items-center gap-3">
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="input-field w-auto"
            >
              <option value="newest">Newest First</option>
              <option value="oldest">Oldest First</option>
              <option value="title">Title A-Z</option>
              <option value="duration">Duration</option>
            </select>

            <select
              value={filterBy}
              onChange={(e) => setFilterBy(e.target.value)}
              className="input-field w-auto"
            >
              <option value="all">All Voices</option>
              <option value="male">Male Voice</option>
              <option value="female">Female Voice</option>
            </select>

            {/* View Mode Toggle */}
            <div className="flex items-center bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded ${viewMode === 'grid' ? 'bg-white shadow-sm' : ''}`}
              >
                <Grid className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 rounded ${viewMode === 'list' ? 'bg-white shadow-sm' : ''}`}
              >
                <List className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-200">
          <div className="flex items-center gap-6 text-sm text-gray-600">
            <span>Total: {audioFiles.length} files</span>
            <span>Filtered: {filteredAndSortedFiles.length} files</span>
            {searchTerm && (
              <span>Search: "{searchTerm}"</span>
            )}
          </div>

          {audioFiles.length > 0 && (
            <div className="text-sm text-gray-600">
              Total Duration: {formatDuration(
                audioFiles.reduce((total, audio) => total + (audio.duration || 0), 0)
              )}
            </div>
          )}
        </div>
      </div>

      {/* Audio Files */}
      {filteredAndSortedFiles.length === 0 ? (
        <div className="card text-center py-16">
          <FileAudio className="w-16 h-16 mx-auto text-gray-400 mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 mb-2">
            {audioFiles.length === 0 ? 'No Audio Files Yet' : 'No Files Match Your Search'}
          </h3>
          <p className="text-gray-600 mb-6">
            {audioFiles.length === 0
              ? 'Start by creating your first audio file from text input.'
              : 'Try adjusting your search terms or filters.'
            }
          </p>
          {audioFiles.length === 0 && (
            <a href="/text-input" className="btn-primary">
              Create First Audio
            </a>
          )}
        </div>
      ) : (
        <div className={viewMode === 'grid'
          ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6'
          : 'space-y-4'
        }>
          {filteredAndSortedFiles.map((audio) => (
            viewMode === 'grid' ? (
              <AudioCard
                key={audio.id}
                audio={audio}
                isPlaying={currentAudio?.id === audio.id && isPlaying}
                onPlay={() => handlePlayPause(audio)}
                onDownload={() => handleDownload(audio)}
                onShare={() => handleShare(audio)}
                onEdit={() => handleEdit(audio)}
                onDelete={() => handleDelete(audio)}
              />
            ) : (
              <div key={audio.id} className="card">
                <div className="flex items-center gap-4">
                  {/* Play Button */}
                  <button
                    onClick={() => handlePlayPause(audio)}
                    className="btn-primary p-3 rounded-full flex-shrink-0"
                  >
                    {currentAudio?.id === audio.id && isPlaying ? (
                      <Pause className="w-4 h-4" />
                    ) : (
                      <Play className="w-4 h-4" />
                    )}
                  </button>

                  {/* Audio Info */}
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-gray-900 truncate">
                      {audio.title}
                    </h3>
                    <p className="text-sm text-gray-600 truncate">
                      {audio.text.substring(0, 100)}...
                    </p>
                    <div className="flex items-center gap-4 mt-1 text-xs text-gray-500">
                      <span>{formatDuration(audio.duration)}</span>
                      <span>{audio.voiceSettings?.gender} • {audio.voiceSettings?.accent}</span>
                      <span>{new Date(audio.createdAt).toLocaleDateString()}</span>
                      <span>{formatFileSize(audio.size)}</span>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button
                      onClick={() => handleDownload(audio)}
                      className="btn-secondary p-2"
                      title="Download"
                    >
                      <Download className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleShare(audio)}
                      className="btn-secondary p-2"
                      title="Share"
                    >
                      <Share className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleEdit(audio)}
                      className="btn-secondary p-2"
                      title="Edit"
                    >
                      <Edit3 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(audio)}
                      className="btn-secondary p-2 text-red-600 hover:bg-red-50"
                      title="Delete"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            )
          ))}
        </div>
      )}

      {/* Edit Audio Modal */}
      {showAudioModal && editingAudio && (
        <AudioModal
          audioData={editingAudio}
          isEditing={true}
          onSave={handleSaveEdit}
          onClose={() => {
            setShowAudioModal(false)
            setEditingAudio(null)
          }}
        />
      )}
      {/* Management Guide Section */}
      <div className="mt-20">
        <div className="bg-gradient-to-r from-primary-600 to-primary-800 rounded-3xl p-10 text-white shadow-xl">
          <h2 className="text-3xl font-bold mb-6">Efficient Audio Asset Management</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
            <div>
              <h3 className="text-xl font-bold mb-3 flex items-center gap-2">
                <Search className="w-5 h-5 text-primary-200" />
                Advanced Organization
              </h3>
              <p className="text-primary-100 leading-relaxed text-sm">
                As your library grows, finding the right audio file shouldn't be a challenge.
                Our <strong>Search</strong> functionality allows you to filter through titles
                and content snippets instantly. Use the <strong>Sort</strong> options to
                organize by newest files, alphabetically, or by duration.
                Whether you have 10 files or 100, our management tools keep your workflow streamlined.
              </p>
            </div>
            <div>
              <h3 className="text-xl font-bold mb-3 flex items-center gap-2">
                <Share className="w-5 h-5 text-primary-200" />
                Export and Collaboration
              </h3>
              <p className="text-primary-100 leading-relaxed text-sm">
                Voice Converter Pro makes it easy to take your audio with you.
                Use the <strong>Download</strong> button to save high-quality WAV files
                directly to your device for use in video projects, podcasts, or
                audiobooks. The <strong>Share</strong> feature allows you to quickly
                distribute your audio details or copy them for collaboration with your team.
              </p>
            </div>
          </div>

          <div className="mt-10 pt-8 border-t border-primary-500/30">
            <h3 className="text-xl font-bold mb-4">Why Local Management Matters</h3>
            <p className="text-primary-50/80 leading-relaxed">
              Managing your audio files effectively ensures that you can repurpose
              your voiceovers across different platforms without needing to re-generate
              them. By saving your frequent conversions, you save bandwidth and
              computational resources while maintaining a fast and responsive user
              experience. Our goal is to provide a comprehensive workspace where
              creative professionals and everyday users can thrive with AI-powered
              speech technology.
            </p>
            <div className="flex gap-4 mt-6">
              <span className="bg-white/10 px-3 py-1 rounded-full text-xs font-semibold">Local Storage</span>
              <span className="bg-white/10 px-3 py-1 rounded-full text-xs font-semibold">Instant Retrieval</span>
              <span className="bg-white/10 px-3 py-1 rounded-full text-xs font-semibold">Metadata Preservation</span>
            </div>
          </div>
        </div>

        <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-8 mb-20">
          <div className="p-6 bg-white rounded-2xl border border-gray-100 shadow-sm">
            <h4 className="font-bold text-gray-900 mb-2">Grid vs List View</h4>
            <p className="text-sm text-gray-600">Switch between visual grid cards or a detailed list view depending on your preference for metadata visibility.</p>
          </div>
          <div className="p-6 bg-white rounded-2xl border border-gray-100 shadow-sm">
            <h4 className="font-bold text-gray-900 mb-2">Voice Profiling</h4>
            <p className="text-sm text-gray-600">Each saved file retains its original voice settings, making it easy to see which profile you used for each project.</p>
          </div>
          <div className="p-6 bg-white rounded-2xl border border-gray-100 shadow-sm">
            <h4 className="font-bold text-gray-900 mb-2">Quick Edits</h4>
            <p className="text-sm text-gray-600">Need to update a title? The edit feature allows you to refine your file organization on the fly without re-generating audio.</p>
          </div>
        </div>
      </div>
    </div>
  )
}

export default MyAudioFiles
